import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { colors, spacing, borderRadius, typography, shadows } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';
import { Spinner } from '@serps/ui-kit/src/components/Spinner';
import { apiGet, apiPost, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';
import { Toast, type ToastVariant } from '../components/Toast';

interface Props { user: AuthUser }

interface DpeSchool {
  id: string; school_code: string; name: string; type: string;
  level: string; district: string; phone: string | null; email: string | null;
  principal_name: string | null;
  license_id: string | null; license_number: string | null;
  license_type: string | null; issue_date: string | null; expiry_date: string | null;
  license_status: string | null; max_capacity: number | null; current_enrolment: number;
  pending_approvals: string; last_inspection: string | null;
}

interface ComplianceDashboard {
  overview: { active_licenses: string; expired_licenses: string; pending_renewal: string; suspended: string; schools_with_pending_approvals: string };
  byRating: Array<{ rating: string; count: string }>;
  expiring90: Array<{ name: string; expiry_date: string; license_number: string; days_remaining: string }>;
  pendingActions: Array<{ school_name: string; action_deadline: string; findings_ms: string | null }>;
}

const LICENSE_STATUS_CFG: Record<string, { ms: string; en: string; color: string }> = {
  ACTIVE:          { ms: 'Aktif',              en: 'Active',         color: '#4ade80' },
  EXPIRED:         { ms: 'Tamat',              en: 'Expired',        color: '#f87171' },
  SUSPENDED:       { ms: 'Digantung',          en: 'Suspended',      color: '#fb923c' },
  REVOKED:         { ms: 'Dibatalkan',         en: 'Revoked',        color: '#9ca3af' },
  PENDING_RENEWAL: { ms: 'Perlu Diperbaharui', en: 'Pending Renewal',color: '#facc15' },
};

const DISTRICTS = ['brunei-muara','tutong','belait','temburong'];
const DIST_LABELS: Record<string, string> = { 'brunei-muara': 'Brunei Muara', tutong: 'Tutong', belait: 'Belait', temburong: 'Temburong' };

export function DPESchoolsPage({ user }: Props) {
  const { lang } = useLang();
  const E = lang === 'en';
  const navigate = useNavigate();
  const [tab,       setTab]      = useState<'schools' | 'dashboard' | 'expiring'>('schools');
  const [schools,   setSchools]  = useState<DpeSchool[]>([]);
  const [total,     setTotal]    = useState(0);
  const [page,      setPage]     = useState(1);
  const [loading,   setLoading]  = useState(false);
  const [error,     setError]    = useState<string | null>(null);
  const [toast,     setToast]    = useState<{ message: string; variant: ToastVariant } | null>(null);

  const [dashboard, setDashboard]  = useState<ComplianceDashboard | null>(null);
  const [dashLoading, setDashLoading] = useState(false);
  const [dashError,   setDashError]   = useState<string | null>(null);

  const [expiring,     setExpiring]     = useState<unknown[]>([]);
  const [expiryLoading,setExpiryLoading]= useState(false);
  const [expiryError,  setExpiryError]  = useState<string | null>(null);

  const [filterStatus, setFilterStatus] = useState('');
  const [filterDistrict, setFilterDistrict] = useState('');

  // License form
  const [showLicenseForm, setShowLicenseForm] = useState(false);
  const [licForm, setLicForm] = useState({ schoolId: '', licenseNumber: '', licenseType: 'PRIVATE', curriculumType: '', issueDate: '', expiryDate: '', maxCapacity: '' });
  const [licError, setLicError] = useState<string | null>(null);
  const [addingLic, setAddingLic] = useState(false);

  const isAdmin = user.roles.some((r) => ['admin', 'super_admin'].includes(r.role));

  const load = useCallback(async (p: number) => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ page: String(p) });
      if (filterStatus)   params.set('status', filterStatus);
      if (filterDistrict) params.set('district', filterDistrict);
      const result = await apiGet<{ data: DpeSchool[]; total: number }>(`/api/dpe/schools?${params}`);
      setSchools(result.data); setTotal(result.total); setPage(p);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ralat memuatkan sekolah.');
    } finally { setLoading(false); }
  }, [filterStatus, filterDistrict]);

  const loadDashboard = useCallback(async () => {
    setDashLoading(true); setDashError(null);
    try { setDashboard(await apiGet<ComplianceDashboard>('/api/dpe/reports/compliance')); }
    catch (err) { setDashError(err instanceof Error ? err.message : 'Ralat memuatkan papan pemuka.'); }
    finally { setDashLoading(false); }
  }, []);

  const loadExpiring = useCallback(async () => {
    setExpiryLoading(true); setExpiryError(null);
    try { setExpiring(await apiGet<unknown[]>('/api/dpe/licenses/expiring?days=90')); }
    catch (err) { setExpiryError(err instanceof Error ? err.message : 'Gagal memuatkan senarai luput. / Failed to load expiring licenses.'); }
    finally { setExpiryLoading(false); }
  }, []);

  useEffect(() => { if (tab === 'schools')    void load(1);          }, [tab, load]);
  useEffect(() => { if (tab === 'dashboard')  void loadDashboard();  }, [tab, loadDashboard]);
  useEffect(() => { if (tab === 'expiring')   void loadExpiring();   }, [tab, loadExpiring]);

  function validateLicenseForm(): string | null {
    if (!licForm.schoolId.trim())      return 'ID sekolah diperlukan. / School ID required.';
    if (!licForm.licenseNumber.trim()) return 'Nombor lesen diperlukan. / License number required.';
    if (!licForm.issueDate)            return 'Tarikh keluaran diperlukan. / Issue date required.';
    if (!licForm.expiryDate)           return 'Tarikh luput diperlukan. / Expiry date required.';
    if (licForm.expiryDate <= licForm.issueDate) return 'Tarikh luput mesti selepas keluaran. / Expiry must be after issue.';
    if (licForm.maxCapacity && (isNaN(parseInt(licForm.maxCapacity)) || parseInt(licForm.maxCapacity) < 1))
      return 'Kapasiti tidak sah. / Invalid capacity.';
    return null;
  }

  async function handleAddLicense() {
    const err = validateLicenseForm();
    if (err) { setLicError(err); return; }
    setAddingLic(true); setLicError(null);
    try {
      await apiPost('/api/dpe/licenses', {
        schoolId: licForm.schoolId.trim(), licenseNumber: licForm.licenseNumber.trim(),
        licenseType: licForm.licenseType, curriculumType: licForm.curriculumType.trim() || undefined,
        issueDate: licForm.issueDate, expiryDate: licForm.expiryDate,
        maxCapacity: licForm.maxCapacity ? parseInt(licForm.maxCapacity) : undefined,
      });
      setToast({ variant: 'success', message: 'Lesen berjaya didaftarkan. / License registered.' });
      setShowLicenseForm(false);
      setLicForm({ schoolId:'', licenseNumber:'', licenseType:'PRIVATE', curriculumType:'', issueDate:'', expiryDate:'', maxCapacity:'' });
      void load(1);
    } catch (e) {
      setLicError(e instanceof Error ? e.message : 'Gagal mendaftar lesen.');
    } finally { setAddingLic(false); }
  }

  const totalPages = Math.ceil(total / 25) || 1;

  return (
    <>
      <div style={{ minHeight: '100vh', backgroundColor: colors.neutral.black, fontFamily: typography.fontFamily.body, padding: `${spacing.xl} ${spacing.md}` }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>

          <div style={{ marginBottom: spacing.xl }}>
            <Link to="/" style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none' }}>← Laman Utama</Link>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: spacing.xl, gap: spacing.md, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: colors.neutral.white }}>
                🏫 {E ? 'DPE — Private Education Dept.' : 'DPE — Jabatan Pendidikan Swasta'}
              </h1>
              <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                {E ? `Private Education Department — ${total} registered schools` : `Jabatan Pendidikan Swasta — ${total} sekolah berdaftar`}
              </p>
            </div>
            <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
              <Link to="/admin/dpe/inspections" style={{ textDecoration: 'none' }}>
                <Button variant="secondary" size="sm">{E ? '🔍 Inspections' : '🔍 Pemeriksaan'}</Button>
              </Link>
              <Link to="/admin/dpe/approvals" style={{ textDecoration: 'none' }}>
                <Button variant="secondary" size="sm">{E ? '👨‍🏫 Teacher Approvals' : '👨‍🏫 Kelulusan Guru'}</Button>
              </Link>
              <Link to="/admin/dpe/bulletins" style={{ textDecoration: 'none' }}>
                <Button variant="secondary" size="sm">{E ? '📢 Bulletins' : '📢 Buletin'}</Button>
              </Link>
              {isAdmin && !showLicenseForm && (
                <Button variant="primary" size="sm" onClick={() => { setShowLicenseForm(true); setLicError(null); }}>
                  {E ? '+ Register License' : '+ Daftar Lesen'}
                </Button>
              )}
            </div>
          </div>

          {/* License form */}
          {showLicenseForm && (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, marginBottom: spacing.lg }}>
              <p style={{ margin: `0 0 ${spacing.md}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>
                {E ? 'Register Private School License' : 'Daftar Lesen Sekolah Swasta'}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                  <LF label="ID Sekolah *" style={{ flex: '2 1 180px' }}>
                    <input type="text" value={licForm.schoolId} onChange={(e) => setLicForm((f) => ({ ...f, schoolId: e.target.value }))} placeholder="UUID sekolah" disabled={addingLic} style={inputSt()} />
                  </LF>
                  <LF label="Nombor Lesen *" style={{ flex: '2 1 160px' }}>
                    <input type="text" value={licForm.licenseNumber} onChange={(e) => setLicForm((f) => ({ ...f, licenseNumber: e.target.value }))} placeholder="cth: DPE/2024/001" disabled={addingLic} style={inputSt()} maxLength={100} />
                  </LF>
                  <LF label="Jenis Lesen" style={{ flex: '1 1 120px' }}>
                    <select value={licForm.licenseType} onChange={(e) => setLicForm((f) => ({ ...f, licenseType: e.target.value }))} disabled={addingLic} style={inputSt()}>
                      {['PRIVATE','INTERNATIONAL','RELIGIOUS','MONTESSORI','VOCATIONAL'].map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </LF>
                </div>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                  <LF label="Jenis Kurikulum" style={{ flex: '2 1 180px' }}>
                    <input type="text" value={licForm.curriculumType} onChange={(e) => setLicForm((f) => ({ ...f, curriculumType: e.target.value }))} disabled={addingLic} style={inputSt()} maxLength={200} />
                  </LF>
                  <LF label="Tarikh Keluaran *" style={{ flex: '1 1 140px' }}>
                    <input type="date" value={licForm.issueDate} onChange={(e) => setLicForm((f) => ({ ...f, issueDate: e.target.value }))} disabled={addingLic} style={inputSt()} />
                  </LF>
                  <LF label="Tarikh Luput *" style={{ flex: '1 1 140px' }}>
                    <input type="date" value={licForm.expiryDate} min={licForm.issueDate || undefined} onChange={(e) => setLicForm((f) => ({ ...f, expiryDate: e.target.value }))} disabled={addingLic} style={inputSt()} />
                  </LF>
                  <LF label="Kapasiti Maks" style={{ flex: '1 1 100px' }}>
                    <input type="number" value={licForm.maxCapacity} onChange={(e) => setLicForm((f) => ({ ...f, maxCapacity: e.target.value }))} disabled={addingLic} style={inputSt()} min={1} max={9999} />
                  </LF>
                </div>
              </div>
              {licError && <p style={{ margin: `${spacing.sm} 0 0`, color: '#f87171', fontSize: typography.fontSize.sm }}>{licError}</p>}
              <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.md }}>
                <Button variant="primary" size="sm" onClick={() => void handleAddLicense()} disabled={addingLic} loading={addingLic}>
                  {addingLic ? 'Mendaftar…' : 'Simpan Lesen / Save License'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setShowLicenseForm(false); setLicError(null); }} disabled={addingLic}>Batal</Button>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0, marginBottom: spacing.lg, backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, overflow: 'hidden' }}>
            {([['schools', E ? '🏫 Schools' : '🏫 Sekolah'],['dashboard', E ? '📊 Dashboard' : '📊 Papan Pemuka'],['expiring', E ? '⚠ Expiring Soon' : '⚠ Hampir Luput']] as const).map(([t, l]) => (
              <button key={t} onClick={() => setTab(t)} style={{
                flex: 1, padding: `${spacing.sm} ${spacing.xs}`, background: 'none',
                borderBottom: tab === t ? `2px solid ${colors.primary.yellow}` : '2px solid transparent',
                borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                color: tab === t ? colors.primary.yellow : colors.neutral.gray400,
                fontWeight: tab === t ? typography.fontWeight.semibold : typography.fontWeight.medium,
                fontSize: typography.fontSize.sm, cursor: 'pointer', fontFamily: typography.fontFamily.body,
              }}>
                {l}
              </button>
            ))}
          </div>

          {/* ── Schools tab ── */}
          {tab === 'schools' && (
            <>
              <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.lg, marginBottom: spacing.lg }}>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ flex: '1 1 140px' }}>
                    <label style={labelSt}>{E ? 'License Status' : 'Status Lesen'}</label>
                    <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} disabled={loading} style={inputSt()}>
                      <option value="">Semua</option>
                      {Object.entries(LICENSE_STATUS_CFG).map(([v, c]) => <option key={v} value={v}>{E ? c.en : c.ms}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: '1 1 140px' }}>
                    <label style={labelSt}>{E ? 'District' : 'Daerah'}</label>
                    <select value={filterDistrict} onChange={(e) => setFilterDistrict(e.target.value)} disabled={loading} style={inputSt()}>
                      <option value="">{E ? 'All' : 'Semua'}</option>
                      {DISTRICTS.map((d) => <option key={d} value={d}>{DIST_LABELS[d]}</option>)}
                    </select>
                  </div>
                  <Button variant="primary" size="sm" onClick={() => void load(1)} disabled={loading}>
                    {loading ? <Spinner size="sm" /> : E ? 'Filter' : 'Tapis'}
                  </Button>
                </div>
              </div>

              {error && <ErrorBanner msg={error} onRetry={() => void load(page)} />}
              {loading && schools.length === 0 && <LoadingBox />}
              {!loading && !error && schools.length === 0 && <EmptyBox icon="🏫" title={E ? 'No Schools' : 'Tiada Sekolah'} body={E ? 'No private schools found.' : 'Tiada sekolah swasta dijumpai.'} />}

              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm, opacity: loading ? 0.7 : 1 }}>
                {schools.map((sc) => {
                  const lsc  = sc.license_status ? LICENSE_STATUS_CFG[sc.license_status] ?? { ms: sc.license_status, en: sc.license_status, color: '#9ca3af' } : null;
                  const daysLeft = sc.expiry_date ? Math.floor((new Date(sc.expiry_date).getTime() - Date.now()) / 86_400_000) : null;
                  const expWarn  = daysLeft !== null && daysLeft <= 90 && daysLeft > 0 && sc.license_status === 'ACTIVE';
                  const expired  = sc.license_status === 'EXPIRED';

                  return (
                    <div key={sc.id} style={{
                      backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl,
                      border: `1px solid ${expired ? 'rgba(248,113,113,0.4)' : expWarn ? 'rgba(250,204,21,0.3)' : colors.neutral.gray800}`,
                      overflow: 'hidden', boxShadow: shadows.lg,
                    }}>
                      <div style={{ padding: spacing.lg, display: 'flex', gap: spacing.md, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', gap: spacing.xs, marginBottom: spacing.xs, flexWrap: 'wrap', alignItems: 'center' }}>
                            {lsc && <Pill label={E ? lsc.en : lsc.ms} color={lsc.color} />}
                            <Pill label={sc.type.toUpperCase()} color="#a78bfa" />
                            <Pill label={DIST_LABELS[sc.district] ?? sc.district} color="#60a5fa" />
                            {parseInt(sc.pending_approvals) > 0 && (
                              <Pill label={`${sc.pending_approvals} kelulusan menunggu`} color="#fb923c" />
                            )}
                          </div>
                          <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>{sc.name}</p>
                          <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                            {sc.school_code}
                            {sc.principal_name ? ` · ${sc.principal_name}` : ''}
                            {sc.license_number ? ` · ${E ? 'License' : 'Lesen'}: ${sc.license_number}` : ''}
                          </p>
                          {sc.expiry_date && (
                            <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: expWarn ? '#facc15' : expired ? '#f87171' : colors.neutral.gray400 }}>
                              {expired ? '⚠ ' : expWarn ? '⏳ ' : ''}{E ? 'Expires' : 'Luput'}: {new Date(sc.expiry_date).toLocaleDateString(E ? 'en-GB' : 'ms-BN')}
                              {daysLeft !== null && daysLeft > 0 && !expired && ` (${daysLeft} ${E ? 'days left' : 'hari lagi'})`}
                            </p>
                          )}
                          {sc.last_inspection && (
                            <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
                              {E ? 'Last inspection' : 'Pemeriksaan terakhir'}: {new Date(sc.last_inspection).toLocaleDateString(E ? 'en-GB' : 'ms-BN')}
                            </p>
                          )}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/dpe/schools/${sc.id}`)}>
                          {E ? 'View Profile →' : 'Lihat Profil →'}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: spacing.sm, marginTop: spacing.xl }}>
                  <Button variant="ghost" size="sm" disabled={page <= 1 || loading} onClick={() => void load(page - 1)}>{E ? '← Previous' : '← Sebelum'}</Button>
                  <span style={{ fontSize: typography.fontSize.sm, color: colors.neutral.gray400 }}>{page} / {totalPages}</span>
                  <Button variant="ghost" size="sm" disabled={page >= totalPages || loading} onClick={() => void load(page + 1)}>{E ? 'Next →' : 'Seterusnya →'}</Button>
                </div>
              )}
            </>
          )}

          {/* ── Dashboard tab ── */}
          {tab === 'dashboard' && (
            <>
              {dashError && <ErrorBanner msg={dashError} onRetry={() => void loadDashboard()} />}
              {dashLoading && <LoadingBox />}
              {!dashLoading && !dashError && dashboard && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
                  {/* Overview cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: spacing.sm }}>
                    {[
                      { label: E ? 'Active Licenses'    : 'Lesen Aktif',         value: dashboard.overview.active_licenses,    color: '#4ade80' },
                      { label: E ? 'Expired Licenses'   : 'Lesen Tamat',         value: dashboard.overview.expired_licenses,   color: '#f87171' },
                      { label: E ? 'Pending Renewal'    : 'Perlu Diperbaharui',  value: dashboard.overview.pending_renewal,    color: '#facc15' },
                      { label: E ? 'Suspended'          : 'Digantung',           value: dashboard.overview.suspended,          color: '#fb923c' },
                      { label: E ? 'Pending Approvals'  : 'Kelulusan Tertunggak',value: dashboard.overview.schools_with_pending_approvals, color: '#a78bfa' },
                    ].map((item) => (
                      <div key={item.label} style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.lg, textAlign: 'center', border: `1px solid ${item.color}22` }}>
                        <p style={{ margin: 0, fontSize: '28px', fontWeight: typography.fontWeight.bold, color: item.color }}>{item.value ?? '0'}</p>
                        <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>{item.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* By rating */}
                  {dashboard.byRating.length > 0 && (
                    <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.lg }}>
                      <p style={{ margin: `0 0 ${spacing.md}`, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: colors.neutral.gray400 }}>{E ? 'INSPECTION RESULTS (LAST 12 MONTHS)' : 'KEPUTUSAN PEMERIKSAAN (12 BULAN LEPAS)'}</p>
                      <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                        {dashboard.byRating.map((r) => {
                          const color = r.rating === 'COMPLIANT' ? '#4ade80' : r.rating === 'PARTIALLY_COMPLIANT' ? '#facc15' : '#f87171';
                          return (
                            <div key={r.rating} style={{ backgroundColor: `${color}18`, borderRadius: borderRadius.lg, padding: `${spacing.sm} ${spacing.lg}`, textAlign: 'center' }}>
                              <p style={{ margin: 0, fontSize: '24px', fontWeight: typography.fontWeight.bold, color }}>{r.count}</p>
                              <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>{r.rating.replace(/_/g, ' ')}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Pending actions */}
                  {dashboard.pendingActions.length > 0 && (
                    <div style={{ backgroundColor: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: borderRadius.xl, padding: spacing.lg }}>
                      <p style={{ margin: `0 0 ${spacing.sm}`, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: '#f87171' }}>
                        ⚠ {dashboard.pendingActions.length} {E ? 'Pending Actions' : 'Tindakan Belum Selesai'}
                      </p>
                      {dashboard.pendingActions.slice(0, 5).map((a, i) => {
                        const ac = a as { school_name: string; action_deadline: string; findings_ms: string | null };
                        return (
                          <p key={i} style={{ margin: `0 0 ${spacing.xs}`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                            <strong style={{ color: '#f87171' }}>{ac.school_name}</strong>
                            {E ? ' — Action deadline: ' : ' — Tarikh tindakan: '}
                            {ac.action_deadline ? new Date(ac.action_deadline).toLocaleDateString(E ? 'en-GB' : 'ms-BN') : '—'}
                          </p>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── Expiring tab ── */}
          {tab === 'expiring' && (
            <>
              {expiryLoading && <LoadingBox />}
              {!expiryLoading && expiryError && <ErrorBanner msg={expiryError} onRetry={() => void loadExpiring()} />}
              {!expiryLoading && !expiryError && (expiring as Array<{ name: string; expiry_date: string; license_number: string; days_remaining: string }>).map((e, i) => {
                const days = parseInt(e.days_remaining ?? '0') || 0;
                const color = days <= 30 ? '#f87171' : days <= 60 ? '#fb923c' : '#facc15';
                return (
                  <div key={i} style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.lg, padding: spacing.lg, marginBottom: spacing.sm, border: `1px solid ${color}44` }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: spacing.md, flexWrap: 'wrap' }}>
                      <div>
                        <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>{e.name}</p>
                        <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>{E ? 'License' : 'Lesen'}: {e.license_number}</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ margin: 0, fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold, color }}>{days} {E ? 'days' : 'hari'}</p>
                        <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>{new Date(e.expiry_date).toLocaleDateString(E ? 'en-GB' : 'ms-BN')}</p>
                      </div>
                    </div>
                    {days <= 30 && <p style={{ margin: `${spacing.xs} 0 0`, fontSize: typography.fontSize.xs, color: '#f87171' }}>{E ? 'URGENT: Immediate action required.' : 'SEGERA: Tindakan segera diperlukan.'}</p>}
                    {days > 30 && days <= 60 && <p style={{ margin: `${spacing.xs} 0 0`, fontSize: typography.fontSize.xs, color: '#fb923c' }}>{E ? 'Please submit renewal application.' : 'Sila kemukakan permohonan pembaharuan.'}</p>}
                  </div>
                );
              })}
              {!expiryLoading && !expiryError && expiring.length === 0 && <EmptyBox icon="✅" title={E ? 'No Expiring Licenses' : 'Tiada Lesen Hampir Luput'} body={E ? 'No licenses expiring within 90 days.' : 'Tiada lesen yang akan luput dalam 90 hari.'} />}
            </>
          )}
        </div>
      </div>
      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
    </>
  );
}

function LF({ label, children, style }: { label: string; children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={style}><label style={labelSt}>{label}</label>{children}</div>;
}
function Pill({ label, color }: { label: string; color: string }) {
  return <span style={{ display:'inline-block', padding:`1px ${spacing.xs}`, backgroundColor:`${color}22`, color, borderRadius:borderRadius.full, fontSize:typography.fontSize.xs, fontWeight:typography.fontWeight.bold }}>{label}</span>;
}
function ErrorBanner({ msg, onRetry }: { msg: string; onRetry: () => void }) {
  return (
    <div style={{ backgroundColor:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:borderRadius.lg, padding:spacing.lg, marginBottom:spacing.lg }}>
      <p style={{ margin:0, color:'#f87171', fontSize:typography.fontSize.sm }}>{msg}</p>
      <Button variant="ghost" size="sm" onClick={onRetry} style={{ marginTop:spacing.xs }}>Cuba Lagi / Retry</Button>
    </div>
  );
}
function LoadingBox() { return <div style={{ display:'flex', justifyContent:'center', padding:spacing.xl }}><Spinner size="lg" /></div>; }
function EmptyBox({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div style={{ backgroundColor:colors.neutral.charcoal, borderRadius:borderRadius.xl, padding:spacing.xl, textAlign:'center' }}>
      <div style={{ fontSize:'48px', marginBottom:spacing.sm }}>{icon}</div>
      <p style={{ margin:0, fontWeight:typography.fontWeight.semibold, color:colors.neutral.white }}>{title}</p>
      <p style={{ margin:`${spacing.xs} 0 0`, color:colors.neutral.gray400, fontSize:typography.fontSize.sm }}>{body}</p>
    </div>
  );
}
const labelSt: React.CSSProperties = { display:'block', fontSize:typography.fontSize.xs, color:colors.neutral.gray400, marginBottom:spacing.xs };
function inputSt(): React.CSSProperties {
  return { width:'100%', backgroundColor:colors.neutral.black, border:`1px solid ${colors.neutral.gray800}`, borderRadius:borderRadius.md, color:colors.neutral.white, padding:`${spacing.xs} ${spacing.sm}`, fontSize:typography.fontSize.sm, outline:'none', boxSizing:'border-box', fontFamily:typography.fontFamily.body };
}
